/**
 * Execution event envelopes — the control messages a skill run emits.
 *
 * A workflow (and the MCP tool bridge) reports its lifecycle by writing a small
 * JSON object into a chat message's `content`. Those objects are CONTROL
 * messages: a renderer decides whether to draw a status card for one, and every
 * transcript surface must treat one as "not conversation content". They are
 * never text the agent meant to say, so they must never reach a user as a raw
 * JSON bubble.
 *
 * Before this module the type strings were scattered literals — five producers
 * and five independent hardcoded consumer lists, none of which agreed. One kind
 * (`workflow_output_ready`) was emitted and recognised by nothing at all, which
 * is exactly how it ends up printed verbatim to a customer.
 *
 * `EXECUTION_EVENT_ENVELOPE_TYPES` is the single source of truth. Adding a kind
 * here widens the Zod enum, the type, and every guard below at once, and fails
 * the build at `EXECUTION_EVENT_ENVELOPE_STATUS` until its meaning is declared.
 */

import { z } from "zod";

import type { WorkflowExecutionStatusType } from "./message-content.types";

/**
 * Every execution lifecycle event kind a producer may emit.
 *
 * The emitters below type their envelopes as `ExecutionEventEnvelopeInput`, so
 * a kind that is not in this list is a compile error at the emit site rather
 * than a string nobody recognises at runtime:
 * - `tools/tools-internal/tool-agent-config-executor.ts`
 * - `chats/services/chat-assistant/services/tool-executor.service.ts`
 * - `chats/application/use-cases/process-message-workflow.use-case.ts`
 * - `mcp/services/mcp-tools.service.ts`
 */
export const EXECUTION_EVENT_ENVELOPE_TYPES = [
  "workflow_execution_started",
  "workflow_output_ready",
  "workflow_executed",
  "workflow_cancelled",
  "workflow_failed"
] as const;

export const ExecutionEventEnvelopeTypeSchema = z.enum(EXECUTION_EVENT_ENVELOPE_TYPES);

export type ExecutionEventEnvelopeType = z.infer<typeof ExecutionEventEnvelopeTypeSchema>;

/**
 * A producer field that may arrive as a string, as `null`, or not at all.
 *
 * `null` is the case that matters and it is not hypothetical: `Workflow.iconUrl`
 * is a nullable column and several call sites forward it verbatim, so
 * `iconUrl: null` survives `JSON.stringify` into the envelope. A plain
 * `.optional()` rejects `null`, which would fail the whole parse and put the
 * envelope back on the raw-JSON path this module exists to close — for exactly
 * the workflows that have no icon.
 *
 * Normalised to `string | undefined` so every consumer stays on one shape.
 */
const producerString = () =>
  z
    .string()
    .nullish()
    .transform((value) => value ?? undefined);

/**
 * The envelope body. Only `type` is guaranteed — producers populate different
 * subsets, so every other field is optional by contract rather than by accident.
 */
export const ExecutionEventEnvelopeSchema = z.object({
  type: ExecutionEventEnvelopeTypeSchema,
  workflowId: producerString(),
  workflowName: producerString(),
  workflowExecutionId: producerString(),
  workflowStatus: producerString(),
  iconUrl: producerString(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  message: producerString(),
  status: producerString(),
  /** Agent-facing tool result prose. Written for the LLM, never for the user. */
  text: producerString()
});

export type ExecutionEventEnvelope = z.infer<typeof ExecutionEventEnvelopeSchema>;

/**
 * The PRODUCER-side contract: what an emitter is allowed to write.
 *
 * This is the schema's INPUT type, and the difference from
 * `ExecutionEventEnvelope` is load-bearing rather than pedantic. The output
 * type has already run `producerString`'s transform, so it says
 * `iconUrl?: string` — but `Workflow.iconUrl` is a nullable column and several
 * emitters forward it verbatim, so `iconUrl: null` is a value producers really
 * do write. Typing an emitter against the output type would reject exactly the
 * workflows that have no icon.
 *
 * Two properties come from annotating an emitted object with this type:
 *
 * 1. **`type` is closed.** A literal outside `EXECUTION_EVENT_ENVELOPE_TYPES`
 *    fails the build at the emit site. Before this, a producer could invent a
 *    kind, ship it, and have every consumer quietly fail to recognise it — the
 *    exact route by which `workflow_output_ready` came to be emitted while
 *    appearing in no consumer list at all.
 * 2. **The field set is closed.** TypeScript's excess property check rejects a
 *    key this schema does not declare, so a producer cannot smuggle a field
 *    past `ExecutionEventEnvelopeSchema` — which silently drops unknown keys —
 *    and then wonder why consumers never see it.
 */
export type ExecutionEventEnvelopeInput = z.input<typeof ExecutionEventEnvelopeSchema>;

/**
 * The execution status each envelope kind reports.
 *
 * `Record<ExecutionEventEnvelopeType, ...>` is the exhaustiveness anchor: a new
 * entry in `EXECUTION_EVENT_ENVELOPE_TYPES` breaks this object at compile time,
 * so a new kind cannot ship without someone deciding what it means.
 */
export const EXECUTION_EVENT_ENVELOPE_STATUS: Record<
  ExecutionEventEnvelopeType,
  WorkflowExecutionStatusType
> = {
  workflow_execution_started: "RUNNING",
  workflow_output_ready: "RUNNING",
  workflow_executed: "COMPLETED",
  workflow_cancelled: "CANCELLED",
  workflow_failed: "FAILED"
};

/**
 * Whether an envelope kind reports a run that FAILED to deliver a result.
 *
 * Read this instead of testing `envelope.status === "failed"`. `status` is a
 * turn-control signal — `workflow_cancelled` carries `status: "failed"` to mark
 * the call RESOLVED so the agent's turn keeps moving, which is not a claim that
 * anything errored. `type` is the field that carries the distinction, and this
 * table is what turns it into a verdict.
 *
 * `workflow_execution_started` and `workflow_output_ready` are NOT failures: the
 * run is still going and its result arrives later on the async delivery path.
 * A caller that needs "finished successfully" must check for RUNNING separately
 * through {@link EXECUTION_EVENT_ENVELOPE_STATUS} — this table answers only
 * "did something go wrong", which is the question a caller setting a success
 * flag is actually asking.
 *
 * `Record<ExecutionEventEnvelopeType, boolean>` is the exhaustiveness anchor: a
 * new entry in `EXECUTION_EVENT_ENVELOPE_TYPES` breaks this object at compile
 * time, so a new kind cannot ship and default silently into "succeeded".
 */
export const EXECUTION_EVENT_ENVELOPE_FAILED: Record<ExecutionEventEnvelopeType, boolean> = {
  workflow_execution_started: false,
  workflow_output_ready: false,
  workflow_executed: false,
  workflow_cancelled: true,
  workflow_failed: true
};

/** Narrows an unknown value to a known envelope kind. */
export function isExecutionEventEnvelopeType(value: unknown): value is ExecutionEventEnvelopeType {
  return ExecutionEventEnvelopeTypeSchema.safeParse(value).success;
}

function parseJsonObject(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Recovers an envelope from any shape a producer ships it in, or `null`.
 *
 * Three shapes reach consumers and all three have leaked to users:
 * 1. the bare object — persisted as `Message.content` (`mcp-tools.service.ts`)
 * 2. a JSON string — the raw streaming payload
 * 3. `{ text: "<json>" }` — how `completeMessage` wraps a tool result over the
 *    WebSocket, which is the shape the reported customer transcript showed
 *
 * Narrows defensively: arrays, primitives, `null` and unknown `type` values all
 * return `null`, so a plain agent message is never mistaken for an envelope.
 */
export function parseExecutionEventEnvelope(content: unknown): ExecutionEventEnvelope | null {
  if (typeof content === "string") {
    const decoded = parseJsonObject(content);
    return decoded === null ? null : parseExecutionEventEnvelope(decoded);
  }

  if (typeof content !== "object" || content === null || Array.isArray(content)) return null;

  const direct = ExecutionEventEnvelopeSchema.safeParse(content);
  if (direct.success) return direct.data;

  const text = (content as { text?: unknown }).text;
  if (typeof text !== "string") return null;

  const decoded = parseJsonObject(text);
  if (typeof decoded !== "object" || decoded === null || Array.isArray(decoded)) return null;

  const nested = ExecutionEventEnvelopeSchema.safeParse(decoded);
  return nested.success ? nested.data : null;
}

/**
 * True when message content is an execution event envelope in any of its
 * shapes. This is the one question every transcript consumer should ask; the
 * answer decides whether the content is control plumbing or something a user
 * may read.
 */
export function isExecutionEventContent(content: unknown): boolean {
  return parseExecutionEventEnvelope(content) !== null;
}
